'use client';
import { useContext } from 'react';

import {
  IconPlusCircle,
  IconMinusCircle,
  IconChevronUp,
  IconChevronDown,
} from '@veneer/core';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './accordion.module.scss';
import AccordionContext from './accordionContext';
import ItemProps from './ItemProps';
import { GlobalsStyles } from '@monetization/hpaip-components-library';

interface AccordionItemProps {
  appearance?: 'plus' | 'chevron';
  customStyle?: string;
  hasSingleBehavior: boolean;
  index: number;
  item: ItemProps;
  onSingleExpandChange: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
}

interface AccordionIconProps {
  appearance?: 'plus' | 'chevron';
  textColor?: string;
}

const {
  color: { gray12 },
} = GlobalsStyles;

const ExpandIcon = (props: AccordionIconProps) => {
  const { appearance, textColor } = props;
  const { t } = useTranslation();
  if (appearance === 'chevron') {
    return (
      <IconChevronDown
        aria-label={t('accordion.expand')}
        data-testid={'plus'}
        className={styles['icon-plus']}
        style={{ fill: textColor ?? gray12 }}
      />
    );
  }
  return (
    <IconPlusCircle
      aria-label={t('accordion.expand')}
      data-testid={'plus'}
      className={styles['icon-plus']}
    />
  );
};

const CollapseIcon = (props: AccordionIconProps) => {
  const { appearance, textColor } = props;
  const { t } = useTranslation();
  if (appearance === 'chevron') {
    return (
      <IconChevronUp
        aria-label={t('accordion.collapse')}
        data-testid={'minus'}
        className={styles['icon-minus']}
        style={{ fill: textColor ?? gray12 }}
      />
    );
  }
  return (
    <IconMinusCircle
      aria-label={t('accordion.collapse')}
      data-testid={'minus'}
      className={styles['icon-minus']}
      filled={true}
    />
  );
};
const AccordionItem = ({
  appearance = 'plus',
  customStyle = 'item',
  ...props
}: AccordionItemProps) => {
  const { hasSingleBehavior, index, item, onSingleExpandChange } = props;
  const accordionContext = useContext(AccordionContext);
  const {
    onCollapse,
    onExpand,
    singleExpandedItem,
    backgroundColor,
    textColor,
    hideBorder,
  } = accordionContext ?? {};
  const { content, header, id: itemId } = item;
  const { 'aria-label': headerAriaLabel, id: headerId } = header ?? {};
  const borderNone = { borderBottom: 'none' };

  const isExpanded =
    (hasSingleBehavior && index === singleExpandedItem) ||
    (!hasSingleBehavior && item?.expanded);
  const handleClick = (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();

    if (isExpanded) {
      if (onCollapse) onCollapse(event, index, item);
      if (hasSingleBehavior && index === singleExpandedItem)
        onSingleExpandChange(undefined);
    } else {
      if (onExpand) onExpand(event, index, item);
      if (hasSingleBehavior) onSingleExpandChange(index);
    }
  };

  const handleEvent = (
    event:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    const { type } = event;

    if (type === 'click') {
      handleClick(event as React.MouseEvent<HTMLElement>);
    }

    if (type === 'keydown') {
      const { code } = event as React.KeyboardEvent<HTMLButtonElement>;
      if (code === 'Enter' || code === 'Space') {
        event.preventDefault();
        handleClick();
      }
    }
  };

  return (
    <div
      className={styles[customStyle]}
      style={{
        ...(hideBorder && borderNone),
      }}
    >
      <button
        className={styles['header']}
        style={{
          backgroundColor,
          ...(hideBorder && borderNone),
        }}
        aria-controls={itemId}
        aria-expanded={isExpanded}
        aria-label={headerAriaLabel}
        id={'accordion_item_' + itemId}
        data-testid={'accordion_item_' + itemId}
        onClick={handleEvent}
        onKeyDown={handleEvent}
        tabIndex={0}
      >
        {header && (
          <>
            {header.leadingArea && (
              <div className={styles['header-item']}>{header.leadingArea}</div>
            )}
            {header.centralArea && (
              <div
                data-testid={'title'}
                className={styles['title']}
                style={{
                  color: textColor ?? gray12,
                }}
              >
                {header.centralArea}
              </div>
            )}
            <div className={styles['end-area']}>
              {header.trailingArea && (
                <div className={styles['end-area-content']}>
                  {header.trailingArea}
                </div>
              )}
              {!isExpanded ? (
                <ExpandIcon appearance={appearance} textColor={textColor} />
              ) : (
                <CollapseIcon appearance={appearance} textColor={textColor} />
              )}
            </div>
          </>
        )}
      </button>
      {header?.mainArea && (
        <div className={styles['main-area']}>{header.mainArea}</div>
      )}
      <div
        className={classNames(styles['content-wrapper'], {
          [styles['collapsed']]: !isExpanded,
        })}
      >
        <div
          aria-hidden={!isExpanded}
          aria-labelledby={headerId}
          id={itemId}
          className={styles['content']}
          style={{
            color: textColor ?? gray12,
            backgroundColor,
          }}
          data-testid={'content'}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

AccordionItem.displayName = 'AccordionItem';

export default AccordionItem;
